import * as Sentry from "@sentry/react";
import React from "react";
import type { StoredKeycloakTokens } from "../../auth/keycloakCapacitorTokenStorage.js";
import { SIGNUP_SCOPE } from "../../auth/signup/signupScope.js";

const DEVICE_GRANT_TYPE = "urn:ietf:params:oauth:grant-type:device_code";
const SLOW_DOWN_MS = 5000;

export type DeviceSignInState =
  | { status: "idle" }
  | { status: "requesting" }
  | {
      status: "pending";
      userCode: string;
      verificationUri: string;
      verificationUriComplete: string;
      /** Epoch ms after which the code is no longer valid. */
      expiresAt: number;
    }
  | { status: "success" }
  | { status: "denied" }
  | { status: "expired" }
  | { status: "error" };

interface Options {
  authServerUrl: string;
  realm: string;
  clientId: string;
  onSuccess: (tokens: StoredKeycloakTokens) => void;
}

interface DeviceAuthorization {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  verificationUriComplete: string;
  expiresInSeconds: number;
  intervalSeconds: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseDeviceAuthorization(value: unknown): DeviceAuthorization | null {
  if (!isRecord(value)) return null;
  const {
    device_code,
    user_code,
    verification_uri,
    verification_uri_complete,
    expires_in,
    interval,
  } = value;
  if (
    typeof device_code !== "string" ||
    typeof user_code !== "string" ||
    typeof verification_uri !== "string" ||
    typeof verification_uri_complete !== "string" ||
    typeof expires_in !== "number"
  )
    return null;
  return {
    deviceCode: device_code,
    userCode: user_code,
    verificationUri: verification_uri,
    verificationUriComplete: verification_uri_complete,
    expiresInSeconds: expires_in,
    intervalSeconds: typeof interval === "number" ? interval : 5,
  };
}

function parseTokens(value: unknown): StoredKeycloakTokens | null {
  if (!isRecord(value)) return null;
  const { access_token, refresh_token, id_token } = value;
  if (typeof access_token !== "string" || typeof refresh_token !== "string")
    return null;
  return {
    token: access_token,
    refreshToken: refresh_token,
    idToken: typeof id_token === "string" ? id_token : undefined,
  };
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Keycloak's OAuth 2.0 Device Authorization Grant (RFC 8628) for the app's
 * own client. Requests a code only when `start` is called. The device code and
 * tokens never leave this hook except through `onSuccess`.
 */
export function useDeviceSignIn(options: Options) {
  const [state, setState] = React.useState<DeviceSignInState>({
    status: "idle",
  });
  const optionsRef = React.useRef(options);
  optionsRef.current = options;
  // Bumped by start/cancel/unmount: an older run stops as soon as it notices.
  const runRef = React.useRef(0);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>();

  const stop = React.useCallback(() => {
    runRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }, []);

  React.useEffect(() => stop, [stop]);

  const start = React.useCallback(() => {
    stop();
    const run = runRef.current;
    const isCurrent = () => runRef.current === run;
    const { authServerUrl, realm, clientId } = optionsRef.current;
    const endpoint = `${authServerUrl.replace(/\/+$/, "")}/realms/${encodeURIComponent(realm)}/protocol/openid-connect`;
    setState({ status: "requesting" });

    const fail = (error?: unknown) => {
      if (error !== undefined) Sentry.captureException(error);
      setState({ status: "error" });
    };

    const post = (path: string, params: Record<string, string>) =>
      fetch(`${endpoint}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(params).toString(),
      });

    const poll = async (
      grant: DeviceAuthorization,
      expiresAt: number,
      intervalMs: number
    ) => {
      timerRef.current = setTimeout(async () => {
        if (!isCurrent()) return;
        if (Date.now() >= expiresAt) {
          setState({ status: "expired" });
          return;
        }
        try {
          const response = await post("token", {
            grant_type: DEVICE_GRANT_TYPE,
            client_id: clientId,
            device_code: grant.deviceCode,
          });
          const body = await readJson(response);
          if (!isCurrent()) return;
          if (response.ok) {
            const tokens = parseTokens(body);
            if (!tokens) return fail();
            setState({ status: "success" });
            optionsRef.current.onSuccess(tokens);
            return;
          }
          const code = isRecord(body) ? body.error : undefined;
          if (code === "authorization_pending") {
            void poll(grant, expiresAt, intervalMs);
          } else if (code === "slow_down") {
            void poll(grant, expiresAt, intervalMs + SLOW_DOWN_MS);
          } else if (code === "access_denied") {
            setState({ status: "denied" });
          } else if (code === "expired_token") {
            setState({ status: "expired" });
          } else {
            fail();
          }
        } catch (error) {
          if (isCurrent()) fail(error);
        }
      }, intervalMs);
    };

    void (async () => {
      try {
        const response = await post("auth/device", {
          client_id: clientId,
          scope: SIGNUP_SCOPE,
        });
        const grant = response.ok
          ? parseDeviceAuthorization(await readJson(response))
          : null;
        if (!isCurrent()) return;
        if (!grant) return fail();
        const expiresAt = Date.now() + grant.expiresInSeconds * 1000;
        setState({
          status: "pending",
          userCode: grant.userCode,
          verificationUri: grant.verificationUri,
          verificationUriComplete: grant.verificationUriComplete,
          expiresAt,
        });
        void poll(grant, expiresAt, grant.intervalSeconds * 1000);
      } catch (error) {
        if (isCurrent()) fail(error);
      }
    })();
  }, [stop]);

  const cancel = React.useCallback(() => {
    stop();
    setState({ status: "idle" });
  }, [stop]);

  return { state, start, cancel };
}
